import { isArray } from 'lodash';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import PropTypes from 'prop-types';
import { useScroll } from 'motion/react';
import { HeadingWithFlameHighlights } from '@components/heading-with-flame-highlights';
import { useEffect, useRef, useState } from 'react';
import { BastingChoiceType } from './choice-types/basting';
import { Box, Flex } from '../../grid';
import { CheckboxChoiceType } from './choice-types/checkbox';
import { ChoicePropType } from './prop-types';
import { FilterControl } from './filter-control';
import { P } from '../../typography/new';
import { RadioChoiceType } from './choice-types/radio';
import { Icon } from '../../icons';
import { NavigationButton } from './option-group/navigation-button';
import { BastingFireAnimation } from '../../basting-selector/BastingFireAnimation';

const CHOICE_TYPE = {
  BASTING: 'basting',
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
};

const choiceTypeComponent = {
  [CHOICE_TYPE.BASTING]: BastingChoiceType,
  [CHOICE_TYPE.CHECKBOX]: CheckboxChoiceType,
  [CHOICE_TYPE.RADIO]: RadioChoiceType,
};

const DIRECTION = {
  NEXT: 'next',
  PREV: 'prev',
};

const ChoicePicker = ({ choice, isChoiceInvalid, index, onClick, selectedOptions, isMealPicker }) => {
  const { ProductDetailsState } = useRootStore();
  const optionGroupRef = useRef(null);
  const [showNavigation, setShowNavigation] = useState(false);
  const [navigationControls, setNavigationControls] = useState({
    isPrevButtonEnabled: false,
    isNextButtonEnabled: false,
  });
  const { scrollXProgress, scrollX } = useScroll({
    container: optionGroupRef,
  });

  const handleScroll = scrollProgress => {
    setNavigationControls({
      isPrevButtonEnabled: scrollProgress > 0 && scrollX.get() > 0,
      isNextButtonEnabled: scrollProgress < 1,
    });
  };

  const handleButtonClick = direction => {
    if (!optionGroupRef.current) {
      return;
    }

    optionGroupRef.current.scrollBy({ left: direction === DIRECTION.PREV ? -250 : 250, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!optionGroupRef.current) return () => {};

    const handleResize = () => {
      if (optionGroupRef.current) {
        const isScrollable = optionGroupRef.current.scrollWidth > optionGroupRef.current.clientWidth;
        setShowNavigation(isScrollable);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    // Update navigation button state on mount
    handleScroll(scrollXProgress.get());

    // Update navigation button state on scroll
    const unsubscribe = scrollXProgress.onChange(latest => {
      handleScroll(latest);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollXProgress]);

  const ChoiceComponent = choiceTypeComponent[choice.type] || RadioChoiceType;

  const mealPickerOverrides = isMealPicker
    ? {
        selectedOptions: selectedOptions || ProductDetailsState.valuesForMealChoice(choice),
        options: choice.optionsForMealPicker,
        getItemValue: option => option.groupName || option.value,
        getItemLabel: option => option.groupName || option.label,
      }
    : {};

  return (
    <Box position="relative">
      {choice.type === 'basting' && (
        <BastingFireAnimation
          selectedOptions={
            ProductDetailsState.getOptionsForChoice(choice.name)?.map(orderOption => orderOption.option.displayName) ||
            []
          }
        />
      )}

      <Box px={{ xs: '16px', md: '32px' }}>
        <Flex justifyContent="space-between" alignItems="center" mb="16px">
          <HeadingWithFlameHighlights mb="0" heading={choice.name} variant="section" />

          <Flex alignItems="center" gap="8px">
            {choice.required && (
              <P color={isChoiceInvalid ? 'redPrimary' : 'grey70'} variant="small">
                Required
              </P>
            )}
            {showNavigation && (
              <Flex gap="8px">
                <NavigationButton
                  onClick={() => handleButtonClick(DIRECTION.PREV)}
                  disabled={!navigationControls.isPrevButtonEnabled}
                  title="Scroll backwards"
                >
                  <Icon name="chevronLeft" width="16px" fill="currentColor" />
                </NavigationButton>
                <NavigationButton
                  onClick={() => handleButtonClick(DIRECTION.NEXT)}
                  disabled={!navigationControls.isNextButtonEnabled}
                  title="Scroll forwards"
                >
                  <Icon name="chevronRight" width="16px" fill="currentColor" />
                </NavigationButton>
              </Flex>
            )}
          </Flex>
        </Flex>
        <FilterControl hasDisabledOptions={choice.hasDisabledOptions} />
      </Box>
      <Box position="relative">
        <ChoiceComponent
          ref={optionGroupRef}
          choice={choice}
          hasError={isChoiceInvalid}
          selectedOptions={ProductDetailsState.optionIdsForChoiceName(choice.name)}
          onChange={value => {
            if (onClick) {
              onClick(value);
            } else {
              ProductDetailsState.updateChoice(choice.name, isArray(value) ? value : [value]);
            }
          }}
          index={index}
          {...mealPickerOverrides}
        />
      </Box>
    </Box>
  );
};

ChoicePicker.propTypes = {
  choice: ChoicePropType.isRequired,
  isChoiceInvalid: PropTypes.bool,
  index: PropTypes.number,
  onClick: PropTypes.func,
  isMealPicker: PropTypes.bool,
  selectedOptions: PropTypes.arrayOf(PropTypes.string),
};

ChoicePicker.defaultProps = {
  isChoiceInvalid: false,
  index: undefined,
  onClick: undefined,
  isMealPicker: false,
  selectedOptions: undefined,
};

const ComposedChoicePicker = observer(ChoicePicker);
export { ComposedChoicePicker as ChoicePicker };
